import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import duration from 'dayjs/plugin/duration'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { getCPDataWithParams, getCPDataWithoutParams } from 'src/clinic/services'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import {
  getOverlapDataWithPreviousSlots,
  customSortForDayCalendar,
} from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { BOOKING_SESSION_STATUS, STATUS_SESSION_BOOKED } from 'src/consumer/constants'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
dayjs.extend(utc)
dayjs.extend(duration)
dayjs.extend(customParseFormat)

/**
 * Compute overlap groups for booked slots within a time block.
 * Two slots overlap if their time ranges intersect.
 * Returns a Map: slotId -> { overlapIndex, overlapTotal } so overlapping strips can be laid out side by side.
 * @param {Array} bookedSlotsInDay
 * @param {dayjs} blockStart - start of time block
 * @param {dayjs} blockEnd - end of time block
 * @returns {Map<string, { overlapIndex: number, overlapTotal: number }>}
 */
export function getOverlapGroupsForTimeBlock(bookedSlotsInDay, blockStart, blockEnd) {
  const result = new Map()
  if (!bookedSlotsInDay?.length) return result

  const slotsInBlock = bookedSlotsInDay.filter((slot) => {
    const start = dayjs(slot?.startTimeForDayCal || slot?.startTime)
    const end = dayjs(slot?.endTimeForDayCal || slot?.endTime)
    return start.isValid() && end.isValid() && start.isBefore(blockEnd) && end.isAfter(blockStart)
  })

  if (slotsInBlock.length === 0) return result

  const ranges = slotsInBlock.map((slot) => ({
    slot,
    start: dayjs(slot?.startTimeForDayCal || slot?.startTime),
    end: dayjs(slot?.endTimeForDayCal || slot?.endTime),
  }))

  function rangesOverlap(i, j) {
    return ranges[i].start.isBefore(ranges[j].end) && ranges[i].end.isAfter(ranges[j].start)
  }

  // Union-find to get connected components of overlapping slots
  const parent = ranges.map((_, i) => i)
  const find = (i) => {
    if (parent[i] !== i) parent[i] = find(parent[i])
    return parent[i]
  }
  const union = (i, j) => {
    const pi = find(i)
    const pj = find(j)
    if (pi !== pj) parent[pi] = pj
  }
  for (let i = 0; i < ranges.length; i++) {
    for (let j = i + 1; j < ranges.length; j++) {
      if (rangesOverlap(i, j)) union(i, j)
    }
  }

  // Group indices by component root
  const components = new Map()
  ranges.forEach((_, i) => {
    const root = find(i)
    if (!components.has(root)) components.set(root, [])
    components.get(root).push(i)
  })

  components.forEach((indices) => {
    indices.sort((a, b) => ranges[a].start.valueOf() - ranges[b].start.valueOf())
    const n = indices.length
    indices.forEach((idx, order) => {
      const slot = ranges[idx].slot
      const id = slot?.id || slot?.uid || idx
      result.set(id, { overlapIndex: order, overlapTotal: n })
    })
  })

  return result
}

export async function getPatientVitalForBookedEvent(
  patientId,
  clinicId,
  bookedEventId,
  single = true,
) {
  // Support portal: do not call patient vitals API (not needed on patient listing / receipt).
  if (detectCurrentPortal() === 'support') {
    return single ? null : []
  }

  const requestParams = {
    patientId,
    clinicId,
    bookedEventId,
  }
  const response = await getCPDataWithParams(`/api/secure/patient/vitals`, requestParams)
  if (
    response?.status === 200 &&
    isCommonResponseSuccessfulV2(response) &&
    Array.isArray(response?.data?.data?.vital)
  ) {
    if (single) {
      return response?.data?.data?.vital?.[0]
    }
    return response?.data?.data?.vital
  }
  return null
}

export async function getPatientVitalsForBookedEvents(
  patientId,
  clinicId,
  bookedEventIds = [],
) {
  const ids = [...new Set((bookedEventIds || []).filter(Boolean))]
  if (!patientId || ids.length === 0 || detectCurrentPortal() === 'support') {
    return []
  }
  const requestParams = {
    patientId,
    clinicId,
    bookedEventIds: ids.join(','),
  }
  const response = await getCPDataWithParams('/api/secure/patient/vitals', requestParams)
  if (
    response?.status === 200 &&
    isCommonResponseSuccessfulV2(response) &&
    Array.isArray(response?.data?.data?.vital)
  ) {
    return response.data.data.vital
  }
  return []
}

/**
 * Detects the current portal based on window pathname
 * @returns {string} 'mentor' | 'clinic-assistant' | 'support' | null
 */
function detectCurrentPortal() {
  if (typeof window === 'undefined') return null
  const pathname = window.location.pathname
  
  if (pathname.startsWith('/ca-home/') || pathname.startsWith('/kiosk/') || pathname.startsWith('/ca/')) {
    return 'clinic-assistant'
  }
  if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
    return 'support'
  }
  if (pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/') || pathname.startsWith('/l/')) {
    return 'mentor'
  }
  return null
}

/**
 * Fetches vitals for multiple booked slots asynchronously
 * Uses the appropriate API endpoint based on portal/role
 * @param {Array} bookedSlots - Array of booked slot objects
 * @returns {Promise<Array>} Array of booked slots with vitals data attached
 */
export async function fetchVitalsForBookedSlots(bookedSlots = []) {
  if (!Array.isArray(bookedSlots) || bookedSlots.length === 0) {
    return bookedSlots
  }

  const portal = detectCurrentPortal()
  
  // Support portal doesn't have a vitals endpoint yet - skip for now
  if (portal === 'support') {
    return bookedSlots
  }

  // The backend accepts a comma-separated bookedEventIds batch for one patient. Grouping here
  // changes repeated appointments for the same patient from N requests to one and keeps the
  // enrichment independent of the core appointment paint.
  const groups = new Map()
  bookedSlots.forEach((slot) => {
    if (!slot?.patientId || !slot?.id) return
    const key = `${slot.patientId}::${slot.clinicId || ''}`
    const group = groups.get(key) || {
      patientId: slot.patientId,
      clinicId: slot.clinicId,
      bookedEventIds: [],
    }
    group.bookedEventIds.push(slot.id)
    groups.set(key, group)
  })

  const vitalLists = await Promise.all(
    [...groups.values()].map(async (group) => {
      try {
        return await getPatientVitalsForBookedEvents(
          group.patientId,
          group.clinicId,
          group.bookedEventIds,
        )
      } catch (error) {
        console.error(`Error fetching vitals for patient ${group.patientId}:`, error)
        return []
      }
    }),
  )
  const vitalByBookedEventId = new Map()
  vitalLists.flat().forEach((vital) => {
    if (vital?.bookedEventId && !vitalByBookedEventId.has(vital.bookedEventId)) {
      vitalByBookedEventId.set(vital.bookedEventId, vital)
    }
  })
  return bookedSlots.map((slot) => {
    const vital = vitalByBookedEventId.get(slot?.id)
    return vital ? { ...slot, vital, vitals: [vital] } : slot
  })
}

export function prepareWaitingTimeStringFromBookedSlot(bookedSlot) {
  if (
    !bookedSlot ||
    !bookedSlot.createdTime ||
    !bookedSlot.sessionStatus ||
    bookedSlot?.markedCompleted
  ) {
    return ''
  }

  let bookedDateTime = dayjs.utc(bookedSlot.createdTime).local()
  let now = dayjs()

  // If slot is created on a previous day, return empty string
  const startOfDayNow = now.startOf('day')
  const endOfDayNow = now.endOf('day')

  // Check if bookedDateTime is NOT between startOfDayNow and endOfDayNow
  if (!bookedDateTime.isBetween(startOfDayNow, endOfDayNow, null, '[]')) {
    return ''
  }

  // Case: Slot is in in-progress
  if (bookedSlot.sessionStatus === 'in-progress') {
    if (bookedDateTime.isBefore(now)) {
      return 'Booking not completed'
    } else {
      return `Booked: ${bookedDateTime.format('h:mm a')}`
    }
  }

  // Case: Slot is in not-started
  if (bookedSlot.sessionStatus === 'not-started') {
    if (bookedDateTime.isAfter(now)) {
      return `Booked: ${bookedDateTime.format('h:mm a')}`
    } else {
      let waitingTime = dayjs.duration(now.diff(bookedDateTime))
      let formattedWaitingTime = `${waitingTime.hours()}h ${waitingTime.minutes()}m`
      return `Waiting For: ${formattedWaitingTime}`
    }
  }

  // If none of the cases match, return an empty string
  return ''
}

export function checkIntoCurrentlyPeekedInRoomAsInPerson({ currentClinic }) {}

export function prepareBookedSlotsWithShiftedPositions(bookedSlotsPerUtc, timeZone) {
  return bookedSlotsPerUtc?.map((bookedSlotItem) => {
    const { minutesToAddToThisEvent } = getOverlapDataWithPreviousSlots(
      bookedSlotItem,
      bookedSlotsPerUtc,
    )
    return {
      ...bookedSlotItem,
      endTimeForDayCal: dayjs
        .utc(bookedSlotItem.endTime, 'YYYY-MM-DD HH:mm:ss')
        .utcOffset(decodeURIComponent(timeZone))
        .add(minutesToAddToThisEvent, 'seconds')
        .format(`YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`),
      startTimeForDayCal: dayjs
        .utc(bookedSlotItem.startTime, 'YYYY-MM-DD HH:mm:ss')
        .utcOffset(decodeURIComponent(timeZone))
        .add(minutesToAddToThisEvent, 'seconds')
        .format(`YYYY-MM-DDTHH:mm:ss[${decodeURIComponent(timeZone)}]`),
      wasDelayedBecauseOfOverlaps: minutesToAddToThisEvent > 0 ? true : false,
    }
  })
}

export async function fetchAndProcessBookedSlots({
  apiSignature,
  startTime,
  endTime,
  timeZone,
  forMentor = false,
  includeReportRelatedData = false,
}) {
  try {
    let finalSignatureToUse = apiSignature
    if (!finalSignatureToUse?.includes('?')) {
      finalSignatureToUse = finalSignatureToUse + '?'
    }
    if (finalSignatureToUse.endsWith('?')) {
      finalSignatureToUse += `startTime=${startTime}&endTime=${endTime}&include-report-related-data=${includeReportRelatedData}`
    } else {
      finalSignatureToUse += `&startTime=${startTime}&endTime=${endTime}&include-report-related-data=${includeReportRelatedData}`
    }

    // Add cache-busting parameter to prevent PWA/browser caching
    // This ensures fresh data is always fetched, especially important for PWAs
    // Since we've already added parameters above, we know the URL has a '?' and we can append with '&'
    const cacheBuster = `_t=${Date.now()}`
    finalSignatureToUse += `&${cacheBuster}`

    const response = await getCPDataWithoutParams(finalSignatureToUse)
    if (response?.status !== 200) return null

    // Extract bookedEvents and patientInfo from response
    // According to Clinic-Assistant-Booked-Event.md:
    // - bookedEvents: Patient details (patientId, patientNumber, userId, userName) may be REDACTED (null)
    //   when booking belongs to a clinic-brand the assistant does not manage
    // - patientInfo: Present only when accessPatientDetails = true, contains one entry per unique patientId
    //   When accessPatientContact = false, contact fields (email, mobile, etc.) are blanked
    const bookedSlots = response?.data?.data?.bookedEvents || []
    const patients = response?.data?.data?.patientInfo || []
    const patientContactDetailsVisibleByBookedEventId =
      response?.data?.data?.patientContactDetailsVisibleByBookedEventId || {}

    const bookedSlotsPerUtc = bookedSlots?.sort(customSortForDayCalendar)
    // Merge patientInfo with booked events by patientId
    // Priority: patientInfo fields override booked event fields (which may be redacted)
    const processedSlots = bookedSlotsPerUtc?.map((bInfo) => {
      const patInfo = patients?.find((pInfo) => pInfo?.id === bInfo?.patientId)
      // patientInfo takes precedence over booked event fields (which may be redacted)
      return {
        ...bInfo,
        patientInfo: patInfo || null,
        patientContactDetailsVisible:
          patientContactDetailsVisibleByBookedEventId?.[bInfo?.id] !== false,
      }
    })

    return {
      shiftedSlotBookedEvents: prepareBookedSlotsWithShiftedPositions(processedSlots, timeZone),
      bookedSlotWithPatientInfo: processedSlots,
      cancelledCount: (processedSlots || [])?.filter(
        (item) => item?.status === BOOKING_SESSION_STATUS.CANCELLED,
      )?.length,
    }
  } catch (error) {
    console.error('Error fetching booked slots:', error)
    return null
  }
}

export async function getPaymentLinksForBookedEvents(
  bookedEventIds,
  listOfPaymentLinksApiSignature,
) {
  if (!!listOfPaymentLinksApiSignature && bookedEventIds?.filter((id) => !!id)?.length > 0) {
    const response = await getCPDataWithoutParams(
      `${listOfPaymentLinksApiSignature}?size=500&bookedEventIds=${bookedEventIds
        ?.filter((id) => !!id)
        .join()}`,
    )
    if (response?.status === 200) {
      // setPaymentLinks(response?.data?.content)
      return response?.data?.content
    }
  }
  return []
}

export function shouldShowPrescriptionPdfDownloadButtons({
  forMentor = false,
  bookedSlot,
  prescriptionViewSettingsData,
}) {
  if (!bookedSlot) return false
  if (bookedSlot?.eventType !== 'consultation') return false
  if (bookedSlot?.status !== STATUS_SESSION_BOOKED) return false
  if (bookedSlot?.sessionStatus === BOOKING_SESSION_STATUS.NOT_STARTED) return false
  if (
    getAllRolesInBoolean().isL1TherapySupport ||
    getAllRolesInBoolean().isL2TherapySupport ||
    getAllRolesInBoolean().isL3TherapySupport ||
    getAllRolesInBoolean().isClinicSuccessManager
  ) {
    return true
  }
  if (!forMentor) {
    return false
  }
  if (!prescriptionViewSettingsData) return false
  if (!prescriptionViewSettingsData?.showPdfDownloadRelatedButtons) return false
  return true
}
